import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, School, ExternalLink } from 'lucide-react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { apiService } from '../services/api';
import { buttonClasses } from '../components/ui/Button';

const SchoolConfirmationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  useEffect(() => {
    const confirmRegistration = async () => {
      try {
        // Parameter aus der URL auslesen
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const schoolId = params.get('school');

        if (!token || !schoolId) {
          setStatus('error');
          setMessage('Ungültiger Bestätigungslink. Token oder Schul-ID fehlt.');
          return;
        }

        // API-Aufruf zur Bestätigung der Registrierung
        const response = await apiService.confirmSchoolRegistration(token, schoolId);
        
        if (response.success) {
          setStatus('success');
          setMessage(response.message || 'Schulregistrierung bestätigt.');
          setSchoolName(response.schoolName || '');
          setAdminEmail(response.adminEmail || '');
        } else {
          setStatus('error');
          setMessage(response.error || 'Bei der Bestätigung ist ein Fehler aufgetreten.');
        }
      } catch (error) {
        console.error('Fehler bei der Bestätigung:', error);
        setStatus('error');
        setMessage(error.message || 'Bei der Bestätigung ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
      }
    };

    confirmRegistration();
  }, [location]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center py-12">
            <LoadingSpinner size="large" />
            <h2 className="text-xl font-semibold mt-4 mb-2">Bestätigung wird verarbeitet…</h2>
            <p className="text-muted-foreground">Einen Moment bitte.</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center py-8">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-success-soft flex items-center justify-center rounded-full">
                <Check className="h-10 w-10 text-success" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mt-6 mb-2">
              Registrierung bestätigt
            </h2>
            
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Deine Schule ist bei EachOtter registriert. Die Zugangsdaten gingen an 
              <span className="font-medium"> {adminEmail}</span> gesendet.
            </p>
            
            <div className="bg-primary-soft/40 border border-primary/20 rounded-xl p-6 max-w-lg mx-auto mb-8">
              <School className="h-8 w-8 text-primary mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-foreground mb-1">Nächste Schritte</h3>
              <p className="text-foreground mb-4">Schau in dein E-Mail-Postfach und folge den Schritten aus der Willkommensmail.</p>
              <ol className="text-left text-foreground space-y-2 pl-5 list-decimal">
                <li>Melde dich mit deinen neuen Zugangsdaten an</li>
                <li>Schließe die Schuleinrichtung im Admin-Dashboard ab</li>
                <li>Passe den Skill-Katalog an eure Bedürfnisse an</li>
                <li>Lade deine Kolleg:innen und Schüler:innen ein</li>
              </ol>
            </div>
            
            <motion.button
              onClick={() => navigate('/login')}
              className={buttonClasses('primary', 'lg', 'mx-auto')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Zum Login <ExternalLink className="ml-2 h-4 w-4" />
            </motion.button>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center py-8">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-danger-soft flex items-center justify-center rounded-full">
                <X className="h-10 w-10 text-danger" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mt-6 mb-2">
              Bestätigung fehlgeschlagen
            </h2>
            
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              {message}
            </p>
            
            <div className="bg-danger-soft rounded-xl p-6 max-w-lg mx-auto mb-8">
              <h3 className="text-lg font-semibold text-foreground mb-2">Mögliche Gründe:</h3>
              <ul className="text-left text-foreground space-y-2 pl-5 list-disc">
                <li>Der Bestätigungslink ist abgelaufen (gültig für 7 Tage)</li>
                <li>Die Schule wurde bereits bestätigt</li>
                <li>Der Bestätigungslink ist ungültig oder wurde falsch kopiert</li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => window.location.reload()}
                className={buttonClasses('secondary', 'lg')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Erneut versuchen
              </motion.button>
              
              <motion.button
                onClick={() => navigate('/')}
                className={buttonClasses('primary', 'lg')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Zurück zur Startseite
              </motion.button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-surface-raised flex flex-col">
      <header className="bg-surface border-b border-border py-4">
        <div className="container mx-auto px-4 flex justify-center">
          <img 
            src="/assets/logo.png" 
            alt="EachOtter Logo" 
            width={40}
            height={40}
            className="h-10 w-10"
          />
        </div>
      </header>
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-surface rounded-xl border border-border shadow-token-md overflow-hidden">
          {renderContent()}
        </div>
      </main>
      
      <footer className="bg-surface-raised py-6">
        <div className="container mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} EachOtter. Alle Rechte vorbehalten.</p>
          <p className="mt-2">
            <a href="mailto:support@eachotter.de" className="text-primary hover:underline">
              support@eachotter.de
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SchoolConfirmationPage; 